import React, { useState, useEffect, useRef } from 'react';
import { 
  MissionControlContainer,
  Header,
  Title,
  StatusBadge,
  ActionBar,
  ActionButton,
  TimeFilter,
  TimeOption,
  Grid,
  ClaimsLeakageCard,
  TATCard,
  STPCard,
  FraudDetectionCard,
  DigitizationOverviewCard,
  SectionTitle,
  DigitizationMetrics,
  MetricItem,
  MetricName,
  MetricValue,
  DigitizationChart,
  AgentPerformanceCard,
  AgentList,
  AgentItem,
  AgentIcon,
  AgentInfo,
  AgentName,
  AgentMetrics,
  AgentMetric,
  AgentPerformance,
  ClaimsProcessingCard,
  ProcessingStages,
  StageItem,
  StageIcon,
  StageName,
  StageMetric,
  KPITitle,
  KPIValue,
  KPIChart,
  KPIFooter,
  BackgroundAnimation,
  GridOverlay,
  DataStream,
  GlowEffect
} from '../styles/MissionControlStyles';
import { motion } from 'framer-motion';
import Chart from 'chart.js/auto';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';

const MissionControl = () => {
  const [timeFilter, setTimeFilter] = useState('week');
  const [activeStage, setActiveStage] = useState('verification');
  const digitizationChartRef = useRef(null);
  const leakageChartRef = useRef(null);
  const tatChartRef = useRef(null);
  const stpChartRef = useRef(null);
  const fraudChartRef = useRef(null);
  
  // Mock data for KPIs
  const kpiData = {
    claimsLeakage: {
      value: '$1.2(M)',
      trend: 'down',
      percentage: '15.3%',
      positive: true,
      description: 'Detected by Fraud Waste Abuse Identification Agent',
      history: [1.5, 1.4, 1.3, 1.4, 1.2, 1.1, 1.2]
    },
    tat: {
      value: '2.3(s)',
      unit: 'days',
      trend: 'down',
      percentage: '28.1%',
      positive: true,
      description: 'Average turnaround time by Claims Processing Agents',
      history: [3.2, 3.0, 2.8, 2.5, 2.4, 2.3, 2.3]
    },
    stp: {
      value: '78.5%',
      trend: 'up',
      percentage: '12.4%',
      positive: true,
      description: 'Straight-through processing ratio impacted by all bots',
      history: [66.1, 68.5, 70.2, 72.8, 75.1, 77.3, 78.5]
    },
    fraudDetection: {
      value: '94.2%',
      trend: 'up',
      percentage: '5.8%',
      positive: true,
      description: 'Accuracy of fraud detection system',
      history: [88.4, 89.7, 91.2, 92.5, 93.1, 93.8, 94.2]
    }
  };
  
  // Mock data for digitization metrics
  const digitizationData = {
    totalClaims: {
      value: '12,487',
      trend: 'up',
      percentage: '8.3%'
    },
    automatedClaims: {
      value: '9,802',
      trend: 'up',
      percentage: '15.7%'
    },
    avgProcessingTime: {
      value: '2.3 days',
      trend: 'down',
      percentage: '28.1%'
    },
    costSavings: {
      value: '$2.4M',
      trend: 'up',
      percentage: '32.5%'
    },
    monthlyTrend: [
      { month: 'Jan', manual: 35, automated: 65 },
      { month: 'Feb', manual: 32, automated: 68 },
      { month: 'Mar', manual: 28, automated: 72 },
      { month: 'Apr', manual: 25, automated: 75 },
      { month: 'May', manual: 22, automated: 78 },
      { month: 'Jun', manual: 21, automated: 79 }
    ]
  };
  
  // Mock data for agent performance
  const agentPerformance = [
    {
      name: 'Claims Triage Agent',
      icon: '⚕',
      metrics: [
        { label: 'Accuracy', value: '96.5%', highlight: true, color: 'rgba(16, 185, 129, 0.8)' },
        { label: 'Volume', value: '2,345 claims', highlight: false },
        { label: 'Avg. Time', value: '1.2 min', highlight: true, color: 'rgba(16, 185, 129, 0.8)' }
      ],
      performance: 92
    },
    {
      name: 'Document Extraction Agent',
      icon: '⍚',
      metrics: [
        { label: 'Accuracy', value: '94.8%', highlight: true, color: 'rgba(16, 185, 129, 0.8)' },
        { label: 'Volume', value: '15,782 docs', highlight: false },
        { label: 'Avg. Time', value: '0.8 min', highlight: true, color: 'rgba(16, 185, 129, 0.8)' }
      ],
      performance: 88
    },
    {
      name: 'Fraud Detection Agent',
      icon: '⌬',
      metrics: [
        { label: 'Accuracy', value: '97.2%', highlight: true, color: 'rgba(16, 185, 129, 0.8)' },
        { label: 'Alerts', value: '342 cases', highlight: false },
        { label: 'False Pos.', value: '2.8%', highlight: true, color: 'rgba(16, 185, 129, 0.8)' }
      ],
      performance: 95
    },
    {
      name: 'Claims Processing Agent',
      icon: '⎔',
      metrics: [
        { label: 'STP Rate', value: '78.5%', highlight: true, color: 'rgba(16, 185, 129, 0.8)' },
        { label: 'Volume', value: '9,802 claims', highlight: false },
        { label: 'Avg. TAT', value: '2.3 days', highlight: true, color: 'rgba(16, 185, 129, 0.8)' }
      ],
      performance: 85
    }
  ];
  
  // Mock data for claims processing stages
  const processingStages = [
    { id: 'intake', name: 'Intake', icon: '⌖', completed: true, metric: '100%' },
    { id: 'extraction', name: 'Data Extraction', icon: '⍚', completed: true, metric: '100%' },
    { id: 'verification', name: 'Verification', icon: '⎓', active: true, metric: '68%' },
    { id: 'assessment', name: 'Assessment', icon: '⌾', completed: false, metric: '0%' },
    { id: 'settlement', name: 'Settlement', icon: '⌧', completed: false, metric: '0%' }
  ];
  
  // Animation for background elements
  const [dataStreams, setDataStreams] = useState([]);
  const [glowPositions, setGlowPositions] = useState([
    { top: '20%', left: '30%' },
    { top: '70%', left: '70%' },
    { top: '40%', left: '80%' }
  ]);
  
  useEffect(() => {
    // Generate random data streams
    const streams = [];
    for (let i = 0; i < 20; i++) {
      streams.push({
        id: i,
        height: `${Math.random() * 200 + 100}px`,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        duration: `${Math.random() * 3 + 2}s`
      });
    }
    setDataStreams(streams);
    
    // Animate glow effects
    const interval = setInterval(() => {
      setGlowPositions([
        { 
          top: `${20 + Math.sin(Date.now() / 3000) * 10}%`, 
          left: `${30 + Math.cos(Date.now() / 4000) * 10}%` 
        },
        { 
          top: `${70 + Math.sin(Date.now() / 4000) * 10}%`, 
          left: `${70 + Math.cos(Date.now() / 3000) * 10}%` 
        },
        { 
          top: `${40 + Math.sin(Date.now() / 5000) * 10}%`, 
          left: `${80 + Math.cos(Date.now() / 5000) * 10}%` 
        }
      ]);
    }, 100);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    // Initialize charts
    if (digitizationChartRef.current) {
      const ctx = digitizationChartRef.current.getContext('2d');
      const chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: digitizationData.monthlyTrend.map(item => item.month),
          datasets: [
            {
              label: 'Manual Processing',
              data: digitizationData.monthlyTrend.map(item => item.manual),
              backgroundColor: 'rgba(239, 68, 68, 0.5)',
              borderColor: 'rgba(239, 68, 68, 0.8)',
              borderWidth: 1
            },
            {
              label: 'Automated Processing',
              data: digitizationData.monthlyTrend.map(item => item.automated),
              backgroundColor: 'rgba(16, 185, 129, 0.5)',
              borderColor: 'rgba(16, 185, 129, 0.8)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            },
            y: {
              stacked: true,
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }
          },
          plugins: {
            legend: {
              labels: {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }
          }
        }
      });
      
      return () => chart.destroy();
    }
  }, [digitizationChartRef]);
  
  useEffect(() => {
    // Initialize KPI charts
    const createLineChart = (ref, data, color) => {
      if (ref.current) {
        const ctx = ref.current.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, 60);
        gradient.addColorStop(0, color.replace(')', ', 0.5)').replace('rgb', 'rgba'));
        gradient.addColorStop(1, color.replace(')', ', 0.01)').replace('rgb', 'rgba'));
        
        const chart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['6 mo ago', '5 mo ago', '4 mo ago', '3 mo ago', '2 mo ago', '1 mo ago', 'Current'],
            datasets: [{
              data: data,
              borderColor: color,
              backgroundColor: gradient,
              tension: 0.4,
              fill: true,
              pointRadius: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                enabled: false
              }
            },
            scales: {
              x: {
                display: false
              },
              y: {
                display: false
              }
            },
            elements: {
              line: {
                borderWidth: 2
              }
            }
          }
        });
        
        return () => chart.destroy();
      }
    };
    
    const cleanupLeakage = createLineChart(leakageChartRef, kpiData.claimsLeakage.history, 'rgb(239, 68, 68)');
    const cleanupTAT = createLineChart(tatChartRef, kpiData.tat.history, 'rgb(0, 212, 255)');
    const cleanupSTP = createLineChart(stpChartRef, kpiData.stp.history, 'rgb(16, 185, 129)');
    const cleanupFraud = createLineChart(fraudChartRef, kpiData.fraudDetection.history, 'rgb(245, 158, 11)');
    
    return () => {
      cleanupLeakage && cleanupLeakage();
      cleanupTAT && cleanupTAT();
      cleanupSTP && cleanupSTP();
      cleanupFraud && cleanupFraud();
    };
  }, []);
  
  return (
    <MissionControlContainer>
      <BackgroundAnimation>
        <GridOverlay />
        {dataStreams.map(stream => (
          <DataStream 
            key={stream.id}
            height={stream.height}
            top={stream.top}
            left={stream.left}
            duration={stream.duration}
          />
        ))}
        {glowPositions.map((pos, index) => (
          <GlowEffect key={index} top={pos.top} left={pos.left} />
        ))}
      </BackgroundAnimation>
      
      <Header>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title><FontAwesomeIcon icon={faSlidersH} />Mission Control</Title>
          <StatusBadge>System Operational</StatusBadge>
        </div>
        
        <ActionBar>
          <TimeFilter>
            <TimeOption 
              active={timeFilter === 'day'} 
              onClick={() => setTimeFilter('day')}
            >
              Day
            </TimeOption>
            <TimeOption 
              active={timeFilter === 'week'} 
              onClick={() => setTimeFilter('week')}
            >
              Week
            </TimeOption>
            <TimeOption 
              active={timeFilter === 'month'} 
              onClick={() => setTimeFilter('month')}
            >
              Month
            </TimeOption>
            <TimeOption 
              active={timeFilter === 'quarter'} 
              onClick={() => setTimeFilter('quarter')}
            >
              Quarter
            </TimeOption>
          </TimeFilter>
          
          <ActionButton 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>📊</span> Export Report
          </ActionButton>
        </ActionBar>
      </Header>
      
      <Grid>
        <ClaimsLeakageCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          accentcolor="linear-gradient(90deg, rgba(239, 68, 68, 0.7) 0%, rgba(245, 158, 11, 0.7) 100%)"
        >
          <KPITitle>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
            Claims Leakage
          </KPITitle>
          <KPIValue trend="down" positive={true}>
            {kpiData.claimsLeakage.value} <span>{kpiData.claimsLeakage.percentage}</span>
          </KPIValue>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {kpiData.claimsLeakage.description}
          </div>
          <KPIChart>
            <canvas ref={leakageChartRef}></canvas>
          </KPIChart>
          <KPIFooter>
            <div>vs. Previous Period</div>
            <div>Updated 2 hours ago</div>
          </KPIFooter>
        </ClaimsLeakageCard>
        
        <TATCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          accentcolor="linear-gradient(90deg, rgba(0, 212, 255, 0.7) 0%, rgba(0, 145, 255, 0.7) 100%)"
        >
          <KPITitle>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Turnaround Time
          </KPITitle>
          <KPIValue trend="down" positive={true}>
            {kpiData.tat.value} <span>{kpiData.tat.percentage}</span>
          </KPIValue>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {kpiData.tat.description}
          </div>
          <KPIChart>
            <canvas ref={tatChartRef}></canvas>
          </KPIChart>
          <KPIFooter>
            <div>vs. Previous Period</div>
            <div>Updated 2 hours ago</div>
          </KPIFooter>
        </TATCard>
        
        <STPCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          accentcolor="linear-gradient(90deg, rgba(16, 185, 129, 0.7) 0%, rgba(5, 150, 105, 0.7) 100%)"
        >
          <KPITitle>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            STP Ratio
          </KPITitle>
          <KPIValue trend="up" positive={true}>
            {kpiData.stp.value} <span>{kpiData.stp.percentage}</span>
          </KPIValue>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {kpiData.stp.description}
          </div>
          <KPIChart>
            <canvas ref={stpChartRef}></canvas>
          </KPIChart>
          <KPIFooter>
            <div>vs. Previous Period</div>
            <div>Updated 2 hours ago</div>
          </KPIFooter>
        </STPCard>
        
        <FraudDetectionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          accentcolor="linear-gradient(90deg, rgba(245, 158, 11, 0.7) 0%, rgba(217, 119, 6, 0.7) 100%)"
        >
          <KPITitle>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            Fraud Detection
          </KPITitle>
          <KPIValue trend="up" positive={true}>
            {kpiData.fraudDetection.value} <span>{kpiData.fraudDetection.percentage}</span>
          </KPIValue>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            {kpiData.fraudDetection.description}
          </div>
          <KPIChart>
            <canvas ref={fraudChartRef}></canvas>
          </KPIChart>
          <KPIFooter>
            <div>vs. Previous Period</div>
            <div>Updated 2 hours ago</div>
          </KPIFooter>
        </FraudDetectionCard>
        
        <DigitizationOverviewCard
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <SectionTitle>
            Claims Digitization Overview
            <span>Last 6 months</span>
          </SectionTitle>
          
          <DigitizationMetrics>
            <MetricItem>
              <MetricName>Total Claims Processed</MetricName>
              <MetricValue>{digitizationData.totalClaims.value}</MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricName>Automated Claims</MetricName>
              <MetricValue>{digitizationData.automatedClaims.value}</MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricName>Avg. Processing Time</MetricName>
              <MetricValue>{digitizationData.avgProcessingTime.value}</MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricName>Cost Savings</MetricName>
              <MetricValue>{digitizationData.costSavings.value}</MetricValue>
            </MetricItem>
          </DigitizationMetrics>
          
          <SectionTitle>
            Manual vs. Automated Processing
            <span>Percentage of total claims</span>
          </SectionTitle>
          
          <DigitizationChart>
            <canvas ref={digitizationChartRef}></canvas>
          </DigitizationChart>
        </DigitizationOverviewCard>
        
        <AgentPerformanceCard
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <SectionTitle>
            Agent Performance
            <span>Key metrics</span>
          </SectionTitle>
          
          <AgentList>
            {agentPerformance.map((agent, index) => (
              <AgentItem key={index}>
                <AgentIcon>{agent.icon}</AgentIcon>
                <AgentInfo>
                  <AgentName>{agent.name}</AgentName>
                  <AgentMetrics>
                    {agent.metrics.map((metric, mIndex) => (
                      <AgentMetric 
                        key={mIndex} 
                        highlight={metric.highlight}
                        color={metric.color}
                      >
                        {metric.label}: <span>{metric.value}</span>
                      </AgentMetric>
                    ))}
                  </AgentMetrics>
                </AgentInfo>
                <AgentPerformance value={agent.performance} />
              </AgentItem>
            ))}
          </AgentList>
        </AgentPerformanceCard>
        
        <ClaimsProcessingCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <SectionTitle>
            Claims Processing Pipeline
            <span>Real-time status</span>
          </SectionTitle>
          
          <ProcessingStages>
            {processingStages.map((stage, index) => (
              <StageItem key={index}>
                <StageIcon 
                  active={stage.active} 
                  completed={stage.completed}
                >
                  {stage.icon}
                </StageIcon>
                <StageName active={stage.active}>
                  {stage.name}
                </StageName>
                <StageMetric>
                  {stage.metric}
                </StageMetric>
              </StageItem>
            ))}
          </ProcessingStages>
        </ClaimsProcessingCard>
      </Grid>
    </MissionControlContainer>
  );
};

export default MissionControl;
